import { Link, useLocation, useRouter } from "@tanstack/react-router";
import { MenuIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { LezoWordmark } from "@/components/blocks/brand/LezoLogo";
import { ContactButton } from "@/components/blocks/ContactButton";
import { Button } from "@/components/ui/button";
import {
	NavigationMenu,
	NavigationMenuContent,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
	NavigationMenuTrigger,
	navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "@/components/ui/sheet";
import { ANALYTICS_EVENTS, useAnalytics } from "@/lib/analytics";
import {
	buildLocalizedPath,
	type Locale,
	setLocaleCookie,
	stripLocaleFromPathname,
} from "@/lib/i18n/locale";
import { useI18n } from "@/lib/i18n/use-i18n";
import { USE_CASE_KEYS, USE_CASES } from "@/lib/use-cases";
import { cn } from "@/lib/utils";

type NavTarget =
	| "/{-$locale}/services/applications-metier"
	| "/{-$locale}/services/migration-react-native-expo"
	| "/{-$locale}/cas-usage"
	| (typeof USE_CASES)[keyof typeof USE_CASES]["to"]
	| "/{-$locale}/realisations"
	| "/{-$locale}/produits"
	| "/{-$locale}/a-propos";

type NavLink = { label: string; to: NavTarget; match: string };

const LOCALES = [
	{ value: "fr-FR", label: "FR" },
	{ value: "en-US", label: "EN" },
] as const satisfies { value: Locale; label: string }[];

export default function Header() {
	const router = useRouter();
	const { t, dict, locale, localeParams } = useI18n();
	const { capture } = useAnalytics();
	const [menuOpen, setMenuOpen] = useState(false);
	const [scrolled, setScrolled] = useState(false);

	useEffect(() => {
		const onScroll = () => setScrolled(window.scrollY > 24);
		onScroll();
		window.addEventListener("scroll", onScroll, { passive: true });
		return () => window.removeEventListener("scroll", onScroll);
	}, []);
	const pathname = useLocation({ select: (location) => location.pathname });
	const basePathname = stripLocaleFromPathname(pathname);
	const nav = dict.nav;

	const services: (NavLink & { description: string })[] = [
		{
			label: nav.businessApps,
			description: nav.businessAppsDescription,
			to: "/{-$locale}/services/applications-metier",
			match: "/services/applications-metier",
		},
		{
			label: nav.expoMigration,
			description: nav.expoMigrationDescription,
			to: "/{-$locale}/services/migration-react-native-expo",
			match: "/services/migration-react-native-expo",
		},
	];
	const useCases: (NavLink & { description: string })[] = [
		...USE_CASE_KEYS.map((key) => {
			const { to } = USE_CASES[key];
			return {
				label: dict.useCases.items[key].card.name,
				description: dict.useCases.items[key].card.navDescription,
				to,
				match: to.replace("/{-$locale}", ""),
			};
		}),
		{
			label: nav.useCasesAll,
			description: nav.useCasesAllDescription,
			to: "/{-$locale}/cas-usage",
			match: "/cas-usage",
		},
	];
	const menus = [
		{ label: nav.services, items: services },
		{ label: nav.useCases, items: useCases },
	];
	const links: NavLink[] = [
		{ label: nav.work, to: "/{-$locale}/realisations", match: "/realisations" },
		{ label: nav.products, to: "/{-$locale}/produits", match: "/produits" },
		{ label: nav.about, to: "/{-$locale}/a-propos", match: "/a-propos" },
	];

	const isActive = (link: NavLink) =>
		link.match === "/cas-usage"
			? basePathname === link.match
			: basePathname.startsWith(link.match);

	const switchLocale = (nextLocale: Locale) => {
		setMenuOpen(false);
		if (nextLocale === locale) return;
		capture(ANALYTICS_EVENTS.localeSwitch, {
			from_locale: locale,
			to_locale: nextLocale,
			placement: "header",
		});
		setLocaleCookie(nextLocale);
		router.history.push(
			buildLocalizedPath(
				router.state.location.pathname,
				router.state.location.searchStr,
				router.state.location.hash,
				nextLocale,
			),
		);
	};

	const linkClass = (active: boolean) =>
		cn(
			"transition-colors hover:text-foreground",
			active ? "text-foreground" : "text-muted-foreground",
		);

	const localeSwitcher = (
		<fieldset className="flex items-center gap-1">
			<legend className="sr-only">{t((t) => t.nav.language)}</legend>
			{LOCALES.map((option) => (
				<Button
					key={option.value}
					variant={option.value === locale ? "secondary" : "ghost"}
					size="sm"
					aria-pressed={option.value === locale}
					onClick={() => switchLocale(option.value)}
				>
					{option.label}
				</Button>
			))}
		</fieldset>
	);

	return (
		<header
			className={cn(
				"dark sticky top-0 z-40 border-b text-foreground transition-[background-color,border-color] duration-500",
				scrolled
					? "border-border bg-background/90 backdrop-blur-xl"
					: "border-transparent bg-background",
			)}
		>
			<div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between gap-6 px-6 lg:px-10">
				<Link
					to="/{-$locale}"
					params={localeParams}
					className="group/logo -m-1 flex items-center rounded-sm p-1"
				>
					<LezoWordmark animated title={nav.brand} className="h-7" />
				</Link>

				<NavigationMenu className="hidden font-mono lg:flex [&_a]:text-[13px] [&_button]:text-[13px]">
					<NavigationMenuList>
						{menus.map((menu) => (
							<NavigationMenuItem key={menu.label}>
								<NavigationMenuTrigger
									className={cn(
										"bg-transparent",
										menu.items.some(isActive)
											? "text-foreground"
											: "text-muted-foreground",
									)}
								>
									{menu.label}
								</NavigationMenuTrigger>
								<NavigationMenuContent>
									<ul className="grid w-[26rem] gap-1 p-1">
										{menu.items.map((link) => (
											<li key={link.to}>
												<NavigationMenuLink asChild active={isActive(link)}>
													<Link to={link.to} params={localeParams}>
														<span className="font-medium">{link.label}</span>
														<span className="text-muted-foreground">
															{link.description}
														</span>
													</Link>
												</NavigationMenuLink>
											</li>
										))}
									</ul>
								</NavigationMenuContent>
							</NavigationMenuItem>
						))}
						{links.map((link) => (
							<NavigationMenuItem key={link.to}>
								<NavigationMenuLink
									asChild
									active={isActive(link)}
									className={cn(
										navigationMenuTriggerStyle(),
										"bg-transparent",
										linkClass(isActive(link)),
									)}
								>
									<Link
										to={link.to}
										params={localeParams}
										aria-current={isActive(link) ? "page" : undefined}
									>
										{link.label}
									</Link>
								</NavigationMenuLink>
							</NavigationMenuItem>
						))}
					</NavigationMenuList>
				</NavigationMenu>

				<div className="hidden items-center gap-3 lg:flex">
					{localeSwitcher}
					<ContactButton size="sm" placement="header">
						{nav.contact}
					</ContactButton>
				</div>

				<Sheet open={menuOpen} onOpenChange={setMenuOpen}>
					<SheetTrigger asChild>
						<Button variant="ghost" size="icon" className="lg:hidden">
							<MenuIcon />
							<span className="sr-only">{nav.menu}</span>
						</Button>
					</SheetTrigger>
					<SheetContent side="right" className="overflow-y-auto">
						<SheetHeader>
							<SheetTitle>
								<LezoWordmark title={nav.brand} className="h-5" />
							</SheetTitle>
						</SheetHeader>
						<nav className="flex flex-col gap-6 px-4 text-base font-medium">
							{menus.map((menu) => (
								<div key={menu.label} className="space-y-3">
									<p className="text-sm text-muted-foreground">{menu.label}</p>
									<ul className="space-y-3 border-l pl-4">
										{menu.items.map((link) => (
											<li key={link.to}>
												<Link
													to={link.to}
													params={localeParams}
													onClick={() => setMenuOpen(false)}
													aria-current={isActive(link) ? "page" : undefined}
													className={linkClass(isActive(link))}
												>
													{link.label}
												</Link>
											</li>
										))}
									</ul>
								</div>
							))}
							<ul className="space-y-4">
								{links.map((link) => (
									<li key={link.to}>
										<Link
											to={link.to}
											params={localeParams}
											onClick={() => setMenuOpen(false)}
											aria-current={isActive(link) ? "page" : undefined}
											className={linkClass(isActive(link))}
										>
											{link.label}
										</Link>
									</li>
								))}
							</ul>
						</nav>
						<div className="space-y-4 px-4 pb-6">
							<div onClickCapture={() => setMenuOpen(false)}>
								<ContactButton placement="header_mobile" className="w-full">
									{nav.contact}
								</ContactButton>
							</div>
							{localeSwitcher}
						</div>
					</SheetContent>
				</Sheet>
			</div>
		</header>
	);
}
